'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { Paragraph } from '../ui/paragraph';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ProductBadge } from '../product/detail/product-badge';
import { MdOutlineArrowCircleRight } from 'react-icons/md';
import { htmlExcerpt } from '../../utils/text-excerpt';

export type BlogCardProps = {
  blog: Blog;
  headingTag?: 'h2' | 'h3';
};

export function BlogCard({ blog, headingTag = 'h3' }: BlogCardProps) {
  // The card shows two lines; sending the full article put every post's text into each listing.
  const textContent = htmlExcerpt(blog.content);
  const locale = useLocaleStore(state => state.currentLocale);

  const { t } = useTranslations();

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <Link
      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
      className="generic-hover col-span-1 flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6"
    >
      <div className="relative w-full">
        {blog.categories && blog.categories.length > 0 && (
          <div className="absolute left-2 top-2 z-10 flex items-start justify-start gap-2">
            {blog.categories.map(category => (
              <ProductBadge
                text={category.name}
                key={category.name}
                background="#F1E4DE"
                color="#3E1C12"
              />
            ))}
          </div>
        )}
        <Image
          src={blog?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          width={0}
          height={0}
          className="aspect-square w-full rounded-[4px] object-cover object-center"
          sizes="100vw"
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-3">
        <Paragraph color="muted-foreground">{formattedDate}</Paragraph>
        <Heading title={blog.name} tag="h5" as={headingTag} className={{ title: 'line-clamp-2' }} />
        <Paragraph
          className="line-clamp-2 w-full text-left"
          color="muted-foreground"
          innerHtml={textContent}
        />
      </div>
      {/* A <span>: the whole card is already the link, and a <button> inside <a> is invalid. */}
      <Button asChild variant={'none'} size={'none'} className="text-walnut">
        <span>
          <MdOutlineArrowCircleRight size={18} aria-hidden /> {t('text1148')}
        </span>
      </Button>
    </Link>
  );
}
